import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Button, Checkbox, useShowSnackbar } from "@octopusdeploy/design-system-components";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { borderWidth, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useAggregateAPIOperationStatus, useOctopusClient } from "@octopusdeploy/octopus-react-client";
import type { ProjectTemplateResource, ProjectTemplateProcessResource, ModifyProjectTemplateProcessCommand, PlatformHubVersionControlSettingsResource, ValidateGitRefV2Response, GitRefResource, ProjectTemplateParameterSetResource, } from "@octopusdeploy/octopus-server-client";
import { Repository, mapFromEnvironmentResourceToEnvironmentV2Resource, Permission, ProcessType, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { semanticConventions } from "@octopusdeploy/telemetry";
import { noOp } from "@octopusdeploy/utilities";
import { isEqual, keyBy } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { applyCommonLogicToProducerProcessResource } from "~/areas/platformHub/PlatformHubProducerProcessHelpers";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import { ProjectTemplateCommitDialog } from "~/areas/platformHub/projectTemplates/ProjectTemplateCommitDialog";
import ProjectTemplateOverflowMenu from "~/areas/platformHub/projectTemplates/ProjectTemplateOverflowMenu";
import ProjectTemplatePublishButton from "~/areas/platformHub/projectTemplates/ProjectTemplatePublishButton";
import { projectTemplateStatusActions, type ProjectTemplateStatus } from "~/areas/platformHub/projectTemplates/ProjectTemplateStatus";
import { useProjectTemplateStatusContext } from "~/areas/platformHub/projectTemplates/ProjectTemplateStatusContext";
import { TemplateWizardSteps } from "~/areas/platformHub/projectTemplates/ProjectTemplateWizardDialog";
import { useProjectTemplateWizardDialog } from "~/areas/platformHub/projectTemplates/ProjectTemplateWizardDialogContext";
import useProjectTemplateDeploymentProcessState from "~/areas/platformHub/projectTemplates/useProjectTemplateDeploymentProcessState";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { ProcessController } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorActions, useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { createDefaultFilter, ProcessQueryStringController, useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessSearchFilterContext } from "~/areas/projects/components/Process/Contexts/ProcessSearchFilter/ProcessSearchFilterContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { ProcessListAdvancedFilters } from "~/areas/projects/components/Process/CustomPaperLayouts/ProcessContextPaperLayout";
import ProcessList from "~/areas/projects/components/Process/ProcessList";
import ProcessStepsLayout, { ProcessPageIntent, intentFromFilter } from "~/areas/projects/components/Process/ProcessStepsLayout";
import { emptyVariableSetResource, type ProcessStepsLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import type { ProducerProjectTemplateContextProps } from "~/areas/projects/components/Process/ProjectTemplates/ProducerProjectTemplateContext";
import { buildProducerProjectTemplateContextProps, ProducerProjectTemplateContext } from "~/areas/projects/components/Process/ProjectTemplates/ProducerProjectTemplateContext";
import type { ProcessPageSupportedActions } from "~/areas/projects/components/Process/types";
import { ContextAddStepButton } from "~/areas/projects/components/Steps/index";
import { PersistenceSettingsContextProvider } from "~/areas/projects/context/PersistenceSettingsContext";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import * as tagSets from "~/components/tagSets";
import type { GlobalState } from "~/store/reducers";
import { getParameterSet } from "./ProjectTemplateBranchParametersPage";
import { GetProjectTemplateInitialSuccessCallout } from "./ProjectTemplateLayout";
export interface ProjectTemplateDeploymentProcessQueryParams {
    actionId?: string;
    parentStepId?: string;
    actionType?: string;
    new?: boolean;
    stepTemplates?: boolean;
    childStepTemplates?: boolean;
    templateId?: string;
    templateSpaceId?: string;
    isCommunityTemplate?: boolean;
    actionCategory?: string;
    newlyCreatedProject?: boolean;
    configureProject?: boolean;
    loadSampleProcess?: string;
}
interface ProjectTemplateDeploymentProcessPageProps {
    loaderData: LoaderData;
    gitRef: string;
    queryParams: ProjectTemplateDeploymentProcessQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectTemplateDeploymentProcessQueryParams>;
}
type LoaderData = DataWithProjectTemplate | DataWithFailedGitRefValidationResult;
type DataWithProjectTemplate = {
    projectTemplate: ProjectTemplateResource;
    deploymentProcess: ProjectTemplateProcessResource;
    parameters: ProjectTemplateParameterSetResource;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
};
type DataWithFailedGitRefValidationResult = {
    gitRefValidationResult: ValidateGitRefV2Response;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
};
export async function projectTemplateDeploymentProcessPageLoader(repository: Repository, gitRef: string, slug: string): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const projectTemplate = await repository.ProjectTemplates.get(slug, gitRef);
    const deploymentProcess = await repository.ProjectTemplates.getDeploymentProcess(slug, gitRef);
    const parameters = await getParameterSet(repository, slug, gitRef);
    return {
        projectTemplate,
        deploymentProcess,
        parameters,
        persistenceSettings,
        gitRefValidationResult,
    };
}
const defaultLookups: ProcessStepsLayoutLoaderLookupData = {
    includedScriptModules: [],
    lifecyclePreview: null,
    environmentsById: {},
    environmentsV2ById: {},
    channelsById: null,
    tagSets: [],
    workerPoolsById: {},
    machineRoles: [],
    tagIndex: {},
    userOnboarding: null,
    workerPoolsSummary: {
        WorkerPoolSummaries: [],
        TotalMachines: 0,
        TotalDisabledMachines: 0,
        MachineHealthStatusSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        MachineEndpointSummaries: {
            None: 0,
            TentaclePassive: 0,
            TentacleActive: 0,
            Ssh: 0,
            OfflineDrop: 0,
            AzureWebApp: 0,
            AzureCloudService: 0,
            AzureServiceFabricCluster: 0,
            Kubernetes: 0,
            KubernetesTentacle: 0,
            StepPackage: 0,
        },
        DeploymentTargetSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        TentacleUpgradesRequired: false,
        MachineIdsForCalamariUpgrade: [],
        MachineIdsForTentacleUpgrade: [],
    },
    projectTriggers: [],
    processVariableSet: emptyVariableSetResource(""),
    libraryVariableSets: [],
    templates: [],
    teams: [],
    projects: [],
};
interface ProcessOverviewContentProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    showProcessOverviewStepNotes: boolean;
}
function ProcessOverviewContent({ lookupData, doBusyTask, gitRefResource, showProcessOverviewStepNotes }: ProcessOverviewContentProps) {
    const location = useLocation();
    return <ProcessList lookups={lookupData} doBusyTask={doBusyTask} location={location} gitRefResource={gitRefResource} showStepNotes={showProcessOverviewStepNotes}/>;
}
interface ProcessEditorContentProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    gitRefResource: GitRefResource | undefined;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
function ProcessEditorContent({ lookupData, busy, doBusyTask, errors, gitRefResource, scrollAreaRef }: ProcessEditorContentProps) {
    const isBuiltInWorkerEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled);
    return (<ProcessStepsLayout gitRef={gitRefResource?.CanonicalName} titleAccessory={undefined} stepSlug={undefined} setShowK8sStatusItem={undefined} onProcessSave={() => Promise.resolve()} changeGitRef={noOp} refreshModel={() => Promise.resolve(true)} includedLibraryVariableSetIds={[]} releaseCreationPackageStepId={undefined} lookups={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource} isTenanted={true} onValidationError={() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" })}/>);
}
function isDataWithInvalidGitRef(data: LoaderData, gitRefValidationResult: ValidateGitRefV2Response | undefined): data is DataWithFailedGitRefValidationResult {
    return gitRefValidationResult !== undefined && gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success;
}
export function ProjectTemplateDeploymentProcessPage(props: ProjectTemplateDeploymentProcessPageProps) {
    const { loaderData, gitRef, queryParams } = props;
    const gitRefValidationResult = loaderData.gitRefValidationResult;
    if (isDataWithInvalidGitRef(loaderData, gitRefValidationResult)) {
        return <ProjectTemplateDeploymentProcessErrorPage loaderData={loaderData} gitRef={gitRef}/>;
    }
    return <ProjectTemplateDeploymentProcessPageWithValidData loaderData={loaderData} gitRef={gitRef} queryParams={queryParams}/>;
}
interface ProjectTemplateDeploymentProcessErrorPageProps {
    loaderData: DataWithFailedGitRefValidationResult;
    gitRef: string;
}
function ProjectTemplateDeploymentProcessErrorPage({ loaderData, gitRef }: ProjectTemplateDeploymentProcessErrorPageProps) {
    const persistenceSettings = loaderData.persistenceSettings;
    const error = loaderData.gitRefValidationResult;
    const setGitRef = (newGitRef: string) => {
        lastAccessedPlatformHubGitRef.save(newGitRef);
        window.location.reload();
    };
    return (<PageContent header={{ title: "Process", contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/> }}>
            <PlatformHubVcsError error={error} gitRef={gitRef} persistenceSettings={persistenceSettings} setGitRef={setGitRef}/>
        </PageContent>);
}
interface ProjectTemplateDeploymentProcessPageWithValidDataProps {
    loaderData: DataWithProjectTemplate;
    gitRef: string;
    queryParams: ProjectTemplateDeploymentProcessQueryParams;
}
function ProjectTemplateDeploymentProcessPageWithValidData({ loaderData, gitRef, queryParams }: ProjectTemplateDeploymentProcessPageWithValidDataProps) {
    const navigation = useSpaceAwareNavigation();
    const { projectTemplate } = loaderData;
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress: busy, errors: busyErrors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const client = useOctopusClient();
    const projectTemplateStatus = useProjectTemplateStatusContext();
    const result = useProjectTemplateDeploymentProcessState(projectTemplate, loaderData, gitRef);
    const { deploymentProcessIdentifier, deploymentProcessData, reloadDeploymentProcess, modifyDeploymentProcess } = result;
    const [lookups, setLookups] = useState<ProcessStepsLayoutLoaderLookupData>(defaultLookups);
    const [cleanModel, setCleanModel] = useState<ProjectTemplateProcessResource>(deploymentProcessData.deploymentProcess);
    const [model, setModel] = useState<ProjectTemplateProcessResource>(deploymentProcessData.deploymentProcess);
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const [producerProjectTemplateContextProps, setProducerProjectTemplateContextProps] = useState<ProducerProjectTemplateContextProps>(buildProducerProjectTemplateContextProps(projectTemplate, loaderData.parameters));
    const repository = useMemo(() => new Repository(client, {
        abortSignal: new AbortController().signal,
        correlationContext: { [semanticConventions.componentName]: "ProjectTemplateDeploymentProcessPage" },
    }), [client]);
    const refreshLookups = useCallback(async () => {
        const environments = await repository.Environments.all();
        const tenantTagSets = tagSets.getAllByScopes(["Tenant"]);
        setLookups({
            ...defaultLookups,
            environmentsById: keyBy(environments, "Id"),
            environmentsV2ById: keyBy(environments.map(mapFromEnvironmentResourceToEnvironmentV2Resource), "Id"),
            tagSets: await tenantTagSets,
            machineRoles: await repository.MachineRoles.all(),
            tagIndex: await tagSets.getTagIndex(["Tenant"]),
        });
    }, [repository]);
    useDoBusyTaskEffect(doBusyTask, refreshLookups, [refreshLookups]);
    useEffect(() => {
        setCleanModel(deploymentProcessData.deploymentProcess);
        setModel(deploymentProcessData.deploymentProcess);
    }, [deploymentProcessData.deploymentProcess]);
    const navigateToGitRef = useCallback((newGitRef: string) => {
        const isEditorPage = "actionId" in queryParams || "stepTemplates" in queryParams || "actionType" in queryParams;
        const routeParams = { slug: projectTemplate.Slug, gitRef: newGitRef };
        if (isEditorPage) {
            navigation.redirect(links.projectTemplateDeploymentProcessEditorPage.generateUrl(routeParams, queryParams));
        }
        else {
            navigation.redirect(links.projectTemplateDeploymentProcessPage.generateUrl(routeParams, queryParams));
        }
        lastAccessedPlatformHubGitRef.save(newGitRef);
    }, [navigation, projectTemplate.Slug, queryParams]);
    const supportedActions = (): ProcessPageSupportedActions => {
        return {
            refreshLookupData: refreshLookups,
        };
    };
    const initialFilter = createDefaultFilter();
    // Check if this is editor query params (has editor-specific properties)
    if ("actionId" in queryParams || "stepTemplates" in queryParams || "actionType" in queryParams) {
        const editorParams = queryParams;
        if (editorParams.actionId) {
            initialFilter.actionId = editorParams.actionId;
        }
        if (editorParams.parentStepId) {
            initialFilter.parentStepId = editorParams.parentStepId;
        }
        if (editorParams.actionType) {
            initialFilter.actionType = editorParams.actionType;
        }
        if (editorParams.new !== undefined) {
            initialFilter.new = editorParams.new.toString();
        }
        if (editorParams.stepTemplates !== undefined) {
            initialFilter.stepTemplates = editorParams.stepTemplates ? "1" : "";
        }
        if (editorParams.childStepTemplates !== undefined) {
            initialFilter.childStepTemplates = editorParams.childStepTemplates ? "1" : "";
        }
        if (editorParams.templateId) {
            initialFilter.templateId = editorParams.templateId;
        }
        if (editorParams.templateSpaceId) {
            initialFilter.templateSpaceId = editorParams.templateSpaceId;
        }
        if (editorParams.isCommunityTemplate !== undefined) {
            initialFilter.isCommunityTemplate = editorParams.isCommunityTemplate ? "true" : "false";
        }
        if (editorParams.actionCategory) {
            initialFilter.actionCategory = editorParams.actionCategory;
        }
    }
    return (<ProducerProjectTemplateContext.Provider value={producerProjectTemplateContextProps}>
            <PersistenceSettingsContextProvider {...loaderData.persistenceSettings}>
                <ProcessController layoutActions={supportedActions()} doBusyTask={doBusyTask} processIdentifier={deploymentProcessIdentifier} process={cleanModel} reloadProcess={reloadDeploymentProcess} modifyProcess={modifyDeploymentProcess}>
                    {() => (<ProcessQueryStringController initialQueryFilter={initialFilter}>
                            {() => (<ProjectTemplateDeploymentProcessPageInternal projectTemplate={deploymentProcessData.projectTemplate} cleanModel={cleanModel} model={model} onChange={setModel} lookups={lookups} doBusyTask={doBusyTask} busy={busy} errors={busyErrors} gitRef={gitRef} setGitRef={navigateToGitRef} gitRefResource={loaderData.gitRefValidationResult?.GitRef} persistenceSettings={loaderData.persistenceSettings} scrollAreaRef={scrollAreaRef} modifyDeploymentProcess={modifyDeploymentProcess} projectTemplateStatus={projectTemplateStatus}/>)}
                        </ProcessQueryStringController>)}
                </ProcessController>
            </PersistenceSettingsContextProvider>
        </ProducerProjectTemplateContext.Provider>);
}
interface ProjectTemplateDeploymentProcessPageInternalProps {
    projectTemplate: ProjectTemplateResource;
    cleanModel: ProjectTemplateProcessResource;
    model: ProjectTemplateProcessResource;
    onChange: (model: ProjectTemplateProcessResource) => void;
    lookups: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    busy: boolean;
    errors: Error[];
    gitRef: string;
    setGitRef: (gitRef: string) => void;
    gitRefResource: GitRefResource | undefined;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    modifyDeploymentProcess: (process: ModifyProjectTemplateProcessCommand, gitRef: string | undefined) => Promise<void>;
    projectTemplateStatus: ProjectTemplateStatus | undefined;
}
function ProjectTemplateDeploymentProcessPageInternal({ projectTemplate, cleanModel, model, onChange, lookups, doBusyTask, busy, errors, gitRef, setGitRef, gitRefResource, persistenceSettings, scrollAreaRef, modifyDeploymentProcess, projectTemplateStatus, }: ProjectTemplateDeploymentProcessPageInternalProps) {
    const processContext = useProcessContext();
    const hasValidProcess = processContext.selectors.hasValidProcess();
    const hasSteps = processContext.selectors.hasSteps();
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const errorSelectors = useProcessErrorSelectors();
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const { openDialog: openCommitDialog, isOpen: isCommitDialogOpen, closeAllDialogs: closeCommitDialog } = useDialogTrigger();
    const processSearchFilterContext = useProcessSearchFilterContext();
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    const [showProcessOverviewStepNotes, setShowProcessOverviewStepNotes] = useState(true);
    const processQueryStringContext = useProcessQueryStringContext();
    const queryFilter = processQueryStringContext.state.queryFilter;
    const intent = intentFromFilter(queryFilter);
    const { openWizard, sharingConfiguration, showInitialSuccessCallout, setShowInitialSuccessCallout } = useProjectTemplateWizardDialog();
    const isTemplateShared = sharingConfiguration ? sharingConfiguration.SharedToAllSpaces || sharingConfiguration.IndividuallySharedSpaceIds.length > 0 : false;
    const handleCommit = async (process: ModifyProjectTemplateProcessCommand, gitRef: string | undefined): Promise<void> => {
        try {
            await modifyDeploymentProcess(process, gitRef);
            errorActions.clearErrors();
            warningActions.clearWarnings();
            showSnackbar("Successfully committed changes to the deployment process.");
            // Check if the process now has steps and dispatch action to update status bar
            if (process.Steps && process.Steps.length > 0 && projectTemplateStatus) {
                projectTemplateStatus.dispatch({
                    type: projectTemplateStatusActions.projectTemplateDeploymentProcessAdded,
                });
            }
        }
        catch (error) {
            errorActions.setErrors(getErrorsFromError(error), processContext.selectors);
            warningActions.clearWarnings();
            throw error;
        }
    };
    function getDefaultCommitMessage(): string {
        return "Update deployment process";
    }
    const getProcessToCommit = (): ModifyProjectTemplateProcessCommand => {
        const { modelProcess: clientProcessResource } = processContext.selectors.getProcessesForMerge();
        applyCommonLogicToProducerProcessResource(clientProcessResource, lookups.workerPoolsSummary, processContext.selectors.getActionPlugin, ProcessType.ProjectTemplate);
        return {
            ...model,
            Steps: clientProcessResource.Steps,
            ChangeDescription: getDefaultCommitMessage(),
        };
    };
    const createCommitHandler = () => async (commitMessage: string) => {
        const processToCommit = getProcessToCommit();
        processToCommit.ChangeDescription = commitMessage;
        await handleCommit(processToCommit, gitRef);
    };
    const saveProcess = async (isNavigationConfirmation: boolean) => {
        if (isNavigationConfirmation) {
            openCommitDialog();
        }
    };
    const processFromContext = useMemo(() => (processContext.state.model.process ? processContext.selectors.getProcessResource() : null), [processContext.selectors, processContext.state.model.process]);
    useEffect(() => {
        if (!processFromContext) {
            return;
        }
        if (!isEqual(model.Steps, processFromContext.Steps)) {
            const resource: ProjectTemplateProcessResource = { ...model, Steps: processFromContext.Steps };
            onChange(resource);
        }
    }, [processFromContext, model, onChange]);
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (isTemplateShared) {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish], undefined, createCommitHandler());
                }
                else {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish, TemplateWizardSteps.Share], undefined, createCommitHandler());
                }
            }} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy} onClick={() => handleCommit(getProcessToCommit(), gitRef)}/>
                </div>
            </PermissionCheck>),
    };
    const publishButton: PageAction = {
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ProjectTemplatePublishButton projectTemplate={projectTemplate}/>
            </PermissionCheck>),
        key: "Publish",
    };
    const addStepButton: PageAction = {
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ContextAddStepButton />
            </PermissionCheck>),
        key: "Add Step",
    };
    const projectTemplateOverflowButton: PageAction = {
        type: "custom",
        key: "Project Template Overflow",
        content: <ProjectTemplateOverflowMenu projectTemplate={projectTemplate}/>,
    };
    const processFilters = hasValidProcess && hasSteps && intent === ProcessPageIntent.Unknown
        ? {
            inputs: [
                <FilterSearchBox key="filter-search" placeholder="Filter by name..." value={processSearchFilterContextLookupsState.searchFilter.filterKeyword} onChange={(filterKeyword) => processSearchFilterContextActions.onFilterChange((prev) => ({ ...prev, filterKeyword }))} autoFocus={true}/>,
                <Checkbox key="show-notes" label={"Show notes"} value={showProcessOverviewStepNotes} onChange={setShowProcessOverviewStepNotes} noMargin={true}/>,
            ],
            advancedFilters: {
                content: <ProcessListAdvancedFilters processType={ProcessType.Deployment} environmentLookupData={lookups.environmentsById}/>,
                onResetFilter: () => processSearchFilterContextActions.onFilterChange(() => processSearchFilterContext.getEmptyFilter()),
                hasUserSelectedValues: processSearchFilterContext.isFiltering,
            },
        }
        : undefined;
    const callout = GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName: projectTemplate.Name });
    return (<>
            <LegacyForm devToolsDirtyTrackingKey="Project Template Deployment Process" disableDirtyFormChecking={disableDirtyFormChecking} onSaveClick={saveProcess} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={busy}>
                {({ FormContent }) => (<PageContent busy={busy} header={{
                title: intent === ProcessPageIntent.Unknown ? "Process" : "Process Editor",
                breadcrumbs: intent !== ProcessPageIntent.Unknown
                    ? [
                        {
                            label: "Process",
                            pageUrl: links.projectTemplateDeploymentProcessPage.generateUrl({
                                slug: projectTemplate.Slug,
                                gitRef: gitRef || "main",
                            }),
                        },
                    ]
                    : undefined,
                showBreadcrumbBackIcon: intent !== ProcessPageIntent.Unknown,
                pageActions: intent === ProcessPageIntent.Unknown
                    ? [publishButton, addStepButton, projectTemplateOverflowButton]
                    : intent === ProcessPageIntent.ChooseStepTemplates || intent === ProcessPageIntent.ChooseChildStepTemplates
                        ? [commitButton, projectTemplateOverflowButton]
                        : [publishButton, addStepButton, commitButton, projectTemplateOverflowButton],
                contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/>,
            }} filters={processFilters} callout={callout}>
                        <div className={styles.contentWrapper}>
                            {intent === ProcessPageIntent.Unknown ? (<ProcessOverviewContent lookupData={lookups} doBusyTask={doBusyTask} gitRefResource={gitRefResource} showProcessOverviewStepNotes={showProcessOverviewStepNotes}/>) : (<ProcessEditorContent lookupData={lookups} busy={busy} doBusyTask={doBusyTask} errors={errors} gitRefResource={gitRefResource} scrollAreaRef={scrollAreaRef}/>)}
                        </div>
                    </PageContent>)}
            </LegacyForm>

            <ProjectTemplateCommitDialog open={isCommitDialogOpen} gitRef={gitRef} onClose={closeCommitDialog} onCommit={handleCommit} getCommandToCommit={getProcessToCommit} defaultCommitMessage={getDefaultCommitMessage()} entityName="deployment process"/>
        </>);
}
const styles = {
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
    contentWrapper: css({
        borderTop: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
};
